import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const useFilter = (endpoint, filterProps = {}, token) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFilterProps, setLastFilterProps] = useState(null);

  // Debug log for incoming filterProps
  // console.log("🔍 Incoming filterProps:", {
  //   filterProps,
  //   page: filterProps.page,
  //   limit: filterProps.limit
  // });

  // Memoize filterProps to prevent unnecessary re-renders
  const memoizedFilterProps = useMemo(() => {
    // Remove empty string values to clean the params
    const cleanProps = {};
    Object.entries(filterProps).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        // Handle arrays specially - don't filter out arrays even if they contain zeros
        if (Array.isArray(value)) {
          cleanProps[key] = value;
        } else if (typeof value === 'string' && value.trim() !== '') {
          cleanProps[key] = value;
        } else if (typeof value === 'number' || typeof value === 'boolean') {
          cleanProps[key] = value;
        }
      }
    });

    // Debug log for cleaned props
    // console.log("🧹 Cleaned filterProps:", {
    //   cleanProps,
    //   page: cleanProps.page,
    //   limit: cleanProps.limit
    // });

    return cleanProps;
  }, [
    filterProps.page,
    filterProps.limit,
    filterProps.search,
    filterProps.member,
    filterProps.mobileSignUpStatus,
    filterProps.activityStatus,
    // Other filter props that might change
    JSON.stringify(Object.keys(filterProps).filter(key => 
      !['page', 'limit', 'search', 'member', 'mobileSignUpStatus', 'activityStatus'].includes(key)
    ))
  ]);

  const fetchData = useCallback(async () => {
    if (!endpoint || !token) return;

    // Check if this is a duplicate call with the same params
    const currentPropsString = JSON.stringify(memoizedFilterProps);
    if (lastFilterProps === currentPropsString) {
      // console.log("🔄 Skipping duplicate API call with same params");
      return;
    }

    // console.log("🚀 API Call triggered with params:", {
    //   ...memoizedFilterProps,
    //   page: memoizedFilterProps.page,
    //   limit: memoizedFilterProps.limit
    // });

    setLoading(true);
    setError(null);

    try {
      let response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: memoizedFilterProps,
      });
      
      // console.log("✅ API Response received:", {
      //   pagination: response.data.pagination,
      //   currentPage: response.data.pagination?.currentPage,
      //   totalItems: response.data.pagination?.totalItems
      // });
      
      setData(response.data);
      // Store the params that were used for this successful call
      setLastFilterProps(currentPropsString);
    } catch (err) {
      console.error("❌ API Error:", err);
      setError(err.message || 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  }, [endpoint, memoizedFilterProps, token, lastFilterProps]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    // Force a new fetch by clearing the lastFilterProps
    setLastFilterProps(null);
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch
  };
};

export default useFilter;
